import {
    SponsorshipDurationType,
    SponsorshipPackageTier,
    SponsorshipPriceType,
} from "@prisma/client";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { ApplyToSponsorFormData } from "~/app/events/[slug]/sponsor/_components/types";
import { Button } from "~/components/v3/themed/Button";
import { cn } from "~/utils/fontsConfig";

interface Props {
    currentStep: number;
    selectedTier: SponsorshipPackageTier | null;
    selectedEventIds: Set<string>;
    onNext: () => void;
    onBack?: () => void;
    onCancel?: () => void;
    nextLabel?: string;
    isEventMode?: boolean;
}

const FloatingSponsorIndicator = ({
    currentStep,
    selectedTier,
    selectedEventIds,
    onNext,
    onBack,
    onCancel,
    nextLabel,
    isEventMode = false,
}: Props) => {
    const { register, formState, setValue, watch, getValues } =
        useFormContext<ApplyToSponsorFormData>();
    watch();

    const getDescription = () => {
        if (!selectedTier) return "";
        const eventCount = selectedEventIds.size;

        if (isEventMode) {
            return `Sponsor with ${selectedTier.name} tier`;
        }

        const getProgressText = () => {
            if (selectedTier.durationType === "EVENT_COUNT") {
                return `(${eventCount}/${selectedTier.durationValue} events selected)`;
            }
            if (selectedTier.durationType === "TIME_PERIOD") {
                return `(${eventCount}/1 event selected)`;
            }
            return `(${eventCount} events selected)`;
        };

        switch (selectedTier.durationType) {
            case "EVENT_COUNT":
                return `Sponsor ${selectedTier.durationValue} events with ${selectedTier.name} tier ${getProgressText()}`;
            case "TIME_PERIOD":
                return `Sponsor ${eventCount} event${eventCount > 1 ? "s" : ""} over ${selectedTier.durationValue} month${(selectedTier?.durationValue ?? 0 > 1) ? "s" : ""} with ${selectedTier.name} tier ${getProgressText()}`;
            case "CUSTOM":
                return `Custom sponsorship for ${eventCount} event${eventCount > 1 ? "s" : ""} with ${selectedTier.name} tier ${getProgressText()}`;
            default:
                return `Sponsor ${eventCount} event${eventCount > 1 ? "s" : ""} with ${selectedTier.name} tier ${getProgressText()}`;
        }
    };

    const isCustomDuration = (duration: SponsorshipDurationType) =>
        duration === "CUSTOM";
    const isCustomPrice = (price: SponsorshipPriceType) =>
        price === "CUSTOM_MINIMUM" || price === "CUSTOM_OPEN";

    const [isProceedable, setIsProceedable] = useState(false);
    useEffect(() => {
        const result = canProceed();
        setIsProceedable(result);
    }, [selectedTier, selectedEventIds, currentStep, formState]);
    const canProceed = () => {
        if (!selectedTier) return false;
        // event mode still needs to do step 1 validation
        if (isEventMode && currentStep !== 1) return true;

        switch (currentStep) {
            case 0:
                // Event selection validation
                switch (selectedTier.durationType) {
                    case "EVENT_COUNT":
                        return (
                            selectedEventIds.size === selectedTier.durationValue
                        );
                    case "TIME_PERIOD":
                        return selectedEventIds.size === 1;
                    case "CUSTOM":
                        return selectedEventIds.size > 0;
                    default:
                        return selectedEventIds.size > 0;
                }
            case 1:
                const formValues = getValues();

                // Form validation for Step 2
                const hasRequiredFields =
                    !!formValues.name &&
                    !!formValues.email &&
                    !!formValues.description &&
                    !!formValues.url;

                // Custom price validation
                if (isCustomPrice(selectedTier.priceType)) {
                    const hasCustomPrice =
                        !!formValues.customPriceDollars &&
                        !!formValues.customPriceCents;
                    if (!hasCustomPrice) return false;
                }

                // Custom duration validation
                if (isCustomDuration(selectedTier.durationType)) {
                    const hasCustomDuration = !!formValues.customDuration;
                    if (!hasCustomDuration) return false;
                }

                return hasRequiredFields;
            case 2:
                // Review step - always allow proceeding if we got here
                return true;
            default:
                return false;
        }
    };

    return (
        <AnimatePresence>
            {selectedTier && (
                <motion.div
                    className={cn(`
                        fixed
                        left-0
                        right-0
                        top-[10%]
                        z-[100]
                        mx-auto
                        w-[calc(100%-2rem)]
                        transform
                        rounded-lg
                        border-2
                        border-v2-primary
                        bg-v3-N-400
                        bg-opacity-80
                        p-4
                        shadow-xl
                        backdrop-blur-md
                        md:bottom-4
                        md:top-auto
                        md:w-[600px]
                    `)}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 50 }}
                >
                    <div className="flex w-full items-center justify-between">
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                            <p className="text-sm text-v3-N-100">
                                Selected Package
                            </p>
                            <p className="truncate">{getDescription()}</p>
                            {!isEventMode && !!selectedTier.durationType && (
                                <div className="mt-1 h-1 w-full rounded-full bg-v3-N-300">
                                    <div
                                        className="h-1 rounded-full bg-v3-primary transition-all duration-300"
                                        style={{
                                            width: `${Math.min(
                                                (selectedEventIds.size /
                                                    ((selectedTier?.durationType as SponsorshipDurationType) ===
                                                    "EVENT_COUNT"
                                                        ? (selectedTier?.durationValue as number)
                                                        : 1)) *
                                                    100,
                                                100,
                                            )}%`,
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-4">
                            {onCancel && (
                                <Button
                                    onClick={onCancel}
                                    variant="secondary"
                                    className="shrink-0 whitespace-nowrap"
                                >
                                    Cancel
                                </Button>
                            )}
                            {onBack && (
                                <Button
                                    onClick={onBack}
                                    variant="tertiary"
                                    className="shrink-0 whitespace-nowrap"
                                >
                                    Back
                                </Button>
                            )}
                            <Button
                                onClick={onNext}
                                disabled={!isProceedable}
                                className="shrink-0 whitespace-nowrap"
                            >
                                {nextLabel || "Next"}
                            </Button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default FloatingSponsorIndicator;
